export type MarketSymbol = {
  symbol: string;
  displayName: string;
  group: string;
  assetClass: string;
};

export type MarketSymbolGroup = {
  name: string;
  symbols: [string, string][];
};

export type MarketDiscoveryProfile = {
  favoriteSymbols: string[];
  recentSymbols: string[];
};

export const marketSymbolGroups: MarketSymbolGroup[] = [
  {
    name: "Crypto Spot",
    symbols: [
      ["BTCUSDT", "BTC/USDT"], ["ETHUSDT", "ETH/USDT"],
      ["BNBUSDT", "BNB/USDT"], ["SOLUSDT", "SOL/USDT"],
      ["XRPUSDT", "XRP/USDT"], ["ADAUSDT", "ADA/USDT"],
      ["DOGEUSDT", "DOGE/USDT"], ["AVAXUSDT", "AVAX/USDT"],
      ["LINKUSDT", "LINK/USDT"], ["PAXGUSDT", "PAXG/USDT"],
    ],
  },
  {
    name: "Crypto USD",
    symbols: [["BTC-USD", "BTC/USD"], ["ETH-USD", "ETH/USD"], ["SOL-USD", "SOL/USD"]],
  },
  {
    name: "Stocks & ETF",
    symbols: [
      ["AAPL", "Apple (AAPL)"], ["MSFT", "Microsoft"],
      ["NVDA", "NVIDIA (NVDA)"], ["TSLA", "Tesla (TSLA)"],
      ["AMZN", "Amazon"], ["SPY", "S&P 500 ETF"], ["QQQ", "Nasdaq ETF"],
      ["GLD", "Gold ETF"], ["SLV", "Silver ETF"],
    ],
  },
  {
    name: "Forex",
    symbols: [
      ["EURUSD=X", "EUR/USD"], ["GBPUSD=X", "GBP/USD"],
      ["USDJPY=X", "USD/JPY"], ["AUDUSD=X", "AUD/USD"],
    ],
  },
  {
    name: "Futures & Index",
    symbols: [
      ["XAU/USD", "XAU/USD"], ["SI=F", "Silver"], ["CL=F", "Crude Oil"],
      ["NG=F", "Natural Gas"], ["^GSPC", "S&P 500"], ["^IXIC", "Nasdaq"],
      ["^DJI", "Dow Jones"], ["^VIX", "VIX"],
    ],
  },
];

export const marketSymbolLabels: Record<string, string> = Object.fromEntries(
  marketSymbolGroups.flatMap((group) => group.symbols),
);

// Retain historical labels and stored IDs, but do not offer retired instruments
// as current data sources. This applies to bundled and old cached search results.
const retiredLiveSymbols = new Set(["GLD", "SLV", "GBPUSD=X", "USDJPY=X", "AUDUSD=X", "^GSPC", "^IXIC", "^DJI", "^VIX"]);
export const liveMarketSymbolGroups = marketSymbolGroups.flatMap((group) => {
  const symbols = group.symbols.filter(([symbol]) => !retiredLiveSymbols.has(symbol));
  return symbols.length ? [{ ...group, symbols }] : [];
});
export function isLiveMarketSymbol(symbol: string): boolean {
  return Boolean(marketSymbolLabels[symbol]) && !retiredLiveSymbols.has(symbol);
}

export function searchLocalMarketSymbols(query: string): MarketSymbolGroup[] {
  const terms = normalizedSearch(query).split(" ").filter(Boolean);
  if (!terms.length) return liveMarketSymbolGroups;
  return liveMarketSymbolGroups.flatMap((group) => {
    const symbols = group.symbols.filter(([symbol, label]) => {
      const haystack = normalizedSearch(`${symbol} ${label} ${group.name}`);
      return terms.every((term) => haystack.includes(term));
    });
    return symbols.length ? [{ ...group, symbols }] : [];
  });
}

export function groupRemoteMarketSymbols(items: readonly MarketSymbol[]): MarketSymbolGroup[] {
  const groups = new Map<string, [string, string][]>();
  for (const item of items) {
    if (retiredLiveSymbols.has(item.symbol)) continue;
    const symbols = groups.get(item.group) ?? [];
    symbols.push([item.symbol, item.displayName]);
    groups.set(item.group, symbols);
  }
  return [...groups].map(([name, symbols]) => ({ name, symbols }));
}

export function normalizeMarketDiscoveryProfile(value: unknown): MarketDiscoveryProfile {
  const candidate = value && typeof value === "object"
    ? value as Partial<MarketDiscoveryProfile>
    : {};
  return {
    favoriteSymbols: normalizedSymbolList(candidate.favoriteSymbols, 20),
    recentSymbols: normalizedSymbolList(candidate.recentSymbols, 10),
  };
}

export function toggleFavoriteSymbol(
  profile: MarketDiscoveryProfile,
  symbol: string,
): MarketDiscoveryProfile {
  const normalized = symbol.trim();
  if (!normalized) return profile;
  const existing = profile.favoriteSymbols.filter((item) => item !== normalized);
  return {
    ...profile,
    favoriteSymbols: profile.favoriteSymbols.includes(normalized)
      ? existing
      : [normalized, ...existing].slice(0, 20),
  };
}

export function recordRecentSymbol(
  profile: MarketDiscoveryProfile,
  symbol: string,
): MarketDiscoveryProfile {
  const normalized = symbol.trim();
  if (!normalized) return profile;
  return {
    ...profile,
    recentSymbols: [
      normalized,
      ...profile.recentSymbols.filter((item) => item !== normalized),
    ].slice(0, 10),
  };
}

function normalizedSymbolList(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((item): item is string => (
    typeof item === "string" && Boolean(item.trim()) && item.length <= 64
  )).map((item) => item.trim()))].slice(0, limit);
}

function normalizedSearch(value: string): string {
  return value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
